import React, { useEffect, useMemo } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import PropTypes from "prop-types";

/** Banner fade + rise — unmount after this. */
export const LINE_COMPLETE_BANNER_MS = 1200;
/** Particle lifetime. */
export const LINE_COMPLETE_PARTICLE_MS = 800;
/** Horizontal / vertical glow sweep. */
export const LINE_COMPLETE_SWEEP_MS = 650;
/** Cell scale spring on the tile DOM. */
export const LINE_COMPLETE_SNAP_MS = 420;

const rowSweep = keyframes`
    0% {
        opacity: 0;
        transform: translate3d(-120%, 0, 0);
    }
    12% {
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: translate3d(120%, 0, 0);
    }
`;

const colSweep = keyframes`
    0% {
        opacity: 0;
        transform: translate3d(0, -120%, 0);
    }
    12% {
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: translate3d(0, 120%, 0);
    }
`;

const bannerRise = keyframes`
    0% {
        opacity: 0;
        transform: translate3d(-50%, 8px, 0);
    }
    14% {
        opacity: 1;
        transform: translate3d(-50%, -12px, 0);
    }
    55% {
        opacity: 1;
        transform: translate3d(-50%, -20px, 0);
    }
    100% {
        opacity: 0;
        transform: translate3d(-50%, -28px, 0);
    }
`;

const particleBurst = keyframes`
    0% {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
    }
    100% {
        opacity: 0;
        transform: translate3d(var(--dx), var(--dy), 0) scale(0.35);
    }
`;

/** Spring snap applied on real tile nodes via `cellSnapLockKeyframes`. */
export const cellSnapLockKeyframes = keyframes`
    0% {
        transform: scale(1);
    }
    35% {
        transform: scale(1.06);
    }
    70% {
        transform: scale(0.98);
    }
    100% {
        transform: scale(1);
    }
`;

function buildParticles(rows, cols, gs, seed) {
    const particles = [];
    let n = 0;
    const rand = () => {
        // Deterministic-ish per celebration id so Strict Mode remounts look stable.
        seed = (seed * 1664525 + 1013904223) >>> 0;
        return seed / 0xffffffff;
    };

    for (const row of rows) {
        const yMid = ((row + 0.5) / gs) * 100;
        for (let i = 0; i < 10; i += 1) {
            const fromLeft = i % 2 === 0;
            const angle = (rand() - 0.5) * Math.PI * 0.85;
            const dist = 28 + rand() * 42;
            const dx = (fromLeft ? 1 : -1) * Math.cos(angle) * dist;
            const dy = Math.sin(angle) * dist * 0.65;
            particles.push({
                id: `p-r${row}-${n}`,
                left: fromLeft ? 2 : 98,
                top: yMid + (rand() - 0.5) * (100 / gs) * 0.55,
                dx: `${dx}px`,
                dy: `${dy}px`,
                size: 4 + rand() * 4,
                delay: rand() * 80,
                hue: 95 + rand() * 40,
            });
            n += 1;
        }
    }

    for (const col of cols) {
        const xMid = ((col + 0.5) / gs) * 100;
        for (let i = 0; i < 10; i += 1) {
            const fromTop = i % 2 === 0;
            const angle = (rand() - 0.5) * Math.PI * 0.85;
            const dist = 28 + rand() * 42;
            const dx = Math.sin(angle) * dist * 0.65;
            const dy = (fromTop ? 1 : -1) * Math.cos(angle) * dist;
            particles.push({
                id: `p-c${col}-${n}`,
                left: xMid + (rand() - 0.5) * (100 / gs) * 0.55,
                top: fromTop ? 2 : 98,
                dx: `${dx}px`,
                dy: `${dy}px`,
                size: 4 + rand() * 4,
                delay: rand() * 80,
                hue: 95 + rand() * 40,
            });
            n += 1;
        }
    }

    return particles;
}

/**
 * Non-blocking row/column complete FX over the sliding board.
 * pointer-events: none everywhere — play continues uninterrupted.
 */
export default function SlidingLineCompleteFx({
    fxId,
    gridSize,
    rows = [],
    cols = [],
    praise,
    onComplete,
}) {
    const gs = Number(gridSize) || 1;
    const particles = useMemo(
        () => buildParticles(rows, cols, gs, Number(fxId) || 1),
        [rows, cols, gs, fxId],
    );

    useEffect(() => {
        if (!onComplete) return undefined;
        const timer = window.setTimeout(() => {
            onComplete(fxId);
        }, LINE_COMPLETE_BANNER_MS);
        return () => window.clearTimeout(timer);
    }, [fxId, onComplete]);

    if ((!rows.length && !cols.length) || !praise) return null;

    return (
        <Box
            aria-live="polite"
            aria-atomic="true"
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 8,
                pointerEvents: "none",
                overflow: "hidden",
            }}
        >
            {rows.map((row) => (
                <Box
                    key={`sweep-row-${fxId}-${row}`}
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: `${(row / gs) * 100}%`,
                        width: "42%",
                        height: `${(1 / gs) * 100}%`,
                        background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 25%, rgba(127,184,90,0.75) 50%, rgba(255,255,255,0.2) 75%, transparent 100%)",
                        willChange: "transform, opacity",
                        animation: `${rowSweep} ${LINE_COMPLETE_SWEEP_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                        filter: "blur(0.2px)",
                    }}
                />
            ))}

            {cols.map((col) => (
                <Box
                    key={`sweep-col-${fxId}-${col}`}
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: `${(col / gs) * 100}%`,
                        width: `${(1 / gs) * 100}%`,
                        height: "42%",
                        background:
                            "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.15) 25%, rgba(127,184,90,0.75) 50%, rgba(255,255,255,0.2) 75%, transparent 100%)",
                        willChange: "transform, opacity",
                        animation: `${colSweep} ${LINE_COMPLETE_SWEEP_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                        filter: "blur(0.2px)",
                    }}
                />
            ))}

            {particles.map((p) => (
                <Box
                    key={`${fxId}-${p.id}`}
                    sx={{
                        position: "absolute",
                        left: `${p.left}%`,
                        top: `${p.top}%`,
                        width: p.size,
                        height: p.size,
                        marginLeft: -p.size / 2,
                        marginTop: -p.size / 2,
                        borderRadius: "50%",
                        background: `hsl(${p.hue} 70% 55%)`,
                        boxShadow: `0 0 6px hsl(${p.hue} 80% 60% / 0.85)`,
                        willChange: "transform, opacity",
                        "--dx": p.dx,
                        "--dy": p.dy,
                        animation: `${particleBurst} ${LINE_COMPLETE_PARTICLE_MS}ms ease-out forwards`,
                        animationDelay: `${p.delay}ms`,
                        opacity: 0,
                    }}
                />
            ))}

            <Typography
                key={`banner-${fxId}`}
                component="div"
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: "42%",
                    px: 2.75,
                    py: 1,
                    borderRadius: 999,
                    fontWeight: 800,
                    fontSize: { xs: "1.35rem", sm: "1.7rem" },
                    letterSpacing: 0.5,
                    color: "#fff",
                    textShadow: "0 2px 12px rgba(0,0,0,0.35)",
                    background:
                        "linear-gradient(135deg, rgba(46,125,50,0.95), rgba(127,184,90,0.97))",
                    boxShadow: "0 12px 36px rgba(46, 125, 50, 0.42)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    willChange: "transform, opacity",
                    animation: `${bannerRise} ${LINE_COMPLETE_BANNER_MS}ms ease-out forwards`,
                }}
            >
                {praise}
            </Typography>
        </Box>
    );
}

SlidingLineCompleteFx.propTypes = {
    fxId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    gridSize: PropTypes.number.isRequired,
    rows: PropTypes.arrayOf(PropTypes.number),
    cols: PropTypes.arrayOf(PropTypes.number),
    praise: PropTypes.string,
    onComplete: PropTypes.func,
};
